import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeader from "../components/SectionHeader";

const testimonials = [
  {
    name: "Sarah Chen",
    role: "CTO at TechCorp",
    company: "Fortune 500 Company",
    avatar: "👩‍🦰",
    content: "Tekstation transformed our entire infrastructure. What took our team 6 months, they delivered in 3 weeks. The ROI was evident within the first quarter.",
    rating: 5,
    result: "Reduced infrastructure costs by 42%"
  },
  {
    name: "Michael Rodriguez",
    role: "VP Engineering",
    company: "ScaleUp Startup",
    avatar: "👨‍💻",
    content: "As a startup, we needed enterprise-grade tools without the enterprise complexity. Tekstation gave us exactly that - powerful, scalable, and surprisingly affordable.",
    rating: 5,
    result: "Launched 2 months ahead of schedule"
  },
  {
    name: "Emily Watson",
    role: "Head of Digital Transformation",
    company: "Global Finance Inc",
    avatar: "👩‍💼",
    content: "The AI implementation exceeded our expectations. We're processing data 10x faster and our team is more productive than ever.",
    rating: 5,
    result: "10x improvement in data processing"
  },
  {
    name: "David Kim",
    role: "Founder & CEO",
    company: "Innovation Labs",
    avatar: "👨‍🚀",
    content: "From day one, Tekstation felt like a true partner. They didn't just build our platform; they helped us shape our entire technology strategy.",
    rating: 5,
    result: "Achieved 300% user growth in 6 months"
  }
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section
      id="testimonials"
      className="section-padding"
      style={{ background: "var(--navy2)" }}
      aria-labelledby="testimonials-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Success Stories"
          title="Trusted by Thousands of<br/>Industry Leaders"
          subtitle="See how companies like yours are transforming their business with Tekstation's enterprise platform."
        />

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16"
        >
          {[
            { number: "500+", label: "Companies Served" },
            { number: "99.9%", label: "Uptime SLA" },
            { number: "3.2x", label: "Average ROI" },
            { number: "24/7", label: "Support" }
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 0.5, 
                delay: 0.3 + i * 0.1,
                type: "spring",
                stiffness: 100
              }}
              className="text-center"
            >
              <div className="text-3xl font-bold text-white mb-2">
                {stat.number}
              </div>
              <div className="text-sm text-gray-400">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Testimonials Carousel */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex items-center justify-center gap-4 mb-8"
          >
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`w-3 h-3 rounded-full transition-all ${
                  activeIndex === index
                    ? "bg-blue-500 scale-125"
                    : "bg-gray-600 hover:bg-gray-500"
                }`}
                onClick={() => setActiveIndex(index)}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="grid md:grid-cols-2 gap-8"
            >
              {testimonials.map((testimonial, index) => (
                activeIndex === index && (
                  <motion.div
                    key={testimonial.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative rounded-2xl p-8"
                    style={{
                      background: "linear-gradient(135deg, rgba(26,107,255,0.1), rgba(10,22,40,0.8))",
                      border: "1px solid rgba(26,107,255,0.2)"
                    }}
                  >
                    {/* Star Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={`text-xl ${
                            i < testimonial.rating
                              ? "text-yellow-400"
                              : "text-gray-600"
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>

                    <div className="flex items-start gap-4 mb-6">
                      <div className="text-4xl flex-shrink-0">
                        {testimonial.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-semibold mb-1">
                          {testimonial.name}
                        </div>
                        <div className="text-blue-400 text-sm mb-2">
                          {testimonial.role}
                        </div>
                        <div className="text-gray-400 text-sm">
                          {testimonial.company}
                        </div>
                      </div>
                    </div>

                    <blockquote className="text-gray-300 leading-7 mb-6 italic">
                      "{testimonial.content}"
                    </blockquote>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg"
                      style={{
                        background: "rgba(0,180,130,0.15)",
                        border: "1px solid rgba(0,180,130,0.3)"
                      }}
                    >
                      <span className="text-green-400 text-sm font-medium">📈</span>
                      <span className="text-green-400 text-sm font-medium">
                        {testimonial.result}
                      </span>
                    </motion.div>
                  </motion.div>
                )
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <button
            className="btn-primary text-lg px-8 py-4"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Join 500+ Happy Customers
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
